import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook personnalisé pour gérer les passages de documents
 * 
 * @param {Array} initialPassages - Passages initiaux à charger
 * @param {Function} makeAuthenticatedRequest - Fonction pour effectuer des requêtes authentifiées
 * @param {String} BackEndUrl - URL du backend
 * @param {String} documentId - ID du document
 * @param {String} userId - ID de l'utilisateur
 * @param {String} competenceId - ID de la compétence
 * @param {Function} onError - Fonction de gestion des erreurs
 * @returns {Object} - État et fonctions pour gérer les passages
 */
const usePassageManager = (
  initialPassages = [],
  makeAuthenticatedRequest,
  BackEndUrl,
  documentId,
  userId,
  competenceId,
  onError,
  iframeRef // Référence à l'iframe pour la mise en surbrillance
) => {
  // États
  const [passages, setPassages] = useState([]);
  const [filteredPassages, setFilteredPassages] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Référence pour éviter les courses entre requêtes asynchrones
  const searchRequestCounter = useRef(0);
  
  // Initialisation des passages
  const initializePassages = useCallback((passagesList) => {
    if (!Array.isArray(passagesList) || passagesList.length === 0) {
      return [];
    }
    
    try {
      // Enrichir les passages avec des métadonnées si nécessaire
      const enrichedPassages = passagesList.map(passage => ({
        ...passage,
        id: passage.id || `passage-${Math.random().toString(36).substring(2, 9)}`,
        pageNumber: parseInt(passage.pageNumber) || 1,
        content: passage.content || "",
        concept: passage.concept || null,
        highlights: passage.highlights || {},
        createdAt: passage.createdAt || new Date().toISOString()
      }));
      
      // Mettre à jour l'état
      setPassages(enrichedPassages);
      setFilteredPassages(enrichedPassages);
      
      return enrichedPassages;
    } catch (error) {
      console.error("Erreur lors de l'initialisation des passages:", error);
      onError(error, "Initialisation des passages");
      return [];
    }
  }, [onError]);
  
  // Filtrer les passages selon les filtres actuels
  const updateFilteredPassages = useCallback((passagesList, conceptFilter, showSearchConcepts) => {
    if (!Array.isArray(passagesList)) {
      console.error("Liste de passages invalide pour le filtrage");
      return;
    }
    
    try {
      let filtered = [...passagesList];
      
      // Appliquer le filtre par concept si nécessaire
      if (conceptFilter) {
        filtered = filtered.filter(p => p.concept === conceptFilter);
      } else if (!showSearchConcepts) {
        // Si les concepts de recherche ne sont pas affichés, filtrer ceux marqués comme 'searchConcept'
        filtered = filtered.filter(p => !p.isSearchConcept);
      }
      
      // Mettre à jour l'état
      setFilteredPassages(filtered);
    } catch (error) {
      console.error("Erreur lors du filtrage des passages:", error);
      onError(error, "Filtrage des passages");
    }
  }, [onError]);
  
  // Recherche vectorielle améliorée
  const searchInDocument = useCallback(async (query) => {
    if (!query.trim()) {
      updateFilteredPassages(passages, null, true);
      return [];
    }
    
    try {
      setIsSearching(true);
      
      // Incrémenter le compteur de requêtes pour éviter les courses
      const currentRequestId = ++searchRequestCounter.current;
      
      // Vérifier si la recherche doit être effectuée localement ou via l'API backend
      if (query.length < 3) {
        // Pour les requêtes très courtes, effectuer une recherche locale simple
        console.log("Recherche locale pour requête courte:", query);
        const results = passages.filter(passage => 
          passage.content.toLowerCase().includes(query.toLowerCase())
        );
        
        // Vérifier si la requête est toujours pertinente
        if (currentRequestId === searchRequestCounter.current) {
          updateFilteredPassages(results, null, true);
        }
        return results;
      }
      
      // Effectuer une recherche vectorielle via l'API backend
      console.log("Recherche vectorielle pour:", query);
      
      const searchParams = {
        documentId,
        userId,
        competenceId,
        query: query.trim(),
        options: {
          threshold: 0.4,  // Seuil de similarité sémantique (ajustable)
          limit: 20,       // Nombre maximum de résultats
          includeContent: true
        }
      };
      
      const response = await makeAuthenticatedRequest(
        `${BackEndUrl}/api/searchPassages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(searchParams)
        }
      );
      
      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Réponse API invalide');
      }
      
      // Traiter les résultats de la recherche
      const searchResults = data.passages || [];
      
      // Vérifier si la requête est toujours pertinente
      if (currentRequestId !== searchRequestCounter.current) {
        console.log("Résultats ignorés - requête obsolète");
        return [];
      }
      
      if (searchResults.length === 0) {
        console.log("Aucun résultat trouvé pour la recherche vectorielle, essai de recherche locale");
        
        // Fallback: recherche locale basée sur des mots clés
        const keywordResults = passages.filter(passage => {
          const keywords = query.toLowerCase().split(/\s+/).filter(k => k.length > 3);
          return keywords.some(keyword => passage.content.toLowerCase().includes(keyword));
        });
        
        if (keywordResults.length > 0) {
          console.log(`Recherche par mots clés: ${keywordResults.length} résultats`);
          updateFilteredPassages(keywordResults, null, true);
          return keywordResults;
        }
        
        // Si toujours aucun résultat, afficher un message
        console.log("Aucun résultat trouvé pour la recherche");
        updateFilteredPassages([], null, true);
        return [];
      }
      
      // Mapper les résultats de la recherche avec les passages existants
      const enhancedResults = searchResults.map(result => {
        // Chercher d'abord le passage existant par ID
        const existingPassage = passages.find(p => p.id === result.id);
        
        if (existingPassage) {
          // Conserver les propriétés du passage existant mais ajouter le score de recherche
          return {
            ...existingPassage,
            score: result.score || 0,
            // Ajouter d'autres métadonnées utiles comme des extraits surlignés si disponibles
            highlights: result.highlights || existingPassage.highlights
          };
        } else {
          // Créer un nouveau passage à partir du résultat de recherche
          return {
            id: result.id || `search-result-${Math.random().toString(36).substring(2, 9)}`,
            pageNumber: result.pageNumber || 1,
            content: result.content || "",
            concept: result.concept || null,
            score: result.score || 0,
            highlights: result.highlights || {}
          };
        }
      });
      
      // Trier les résultats par pertinence (score) puis par numéro de page
      const sortedResults = enhancedResults
        .sort((a, b) => {
          // D'abord trier par score de pertinence (descendant)
          const scoreDiff = (b.score || 0) - (a.score || 0);
          if (Math.abs(scoreDiff) > 0.1) return scoreDiff;
          
          // Même score de pertinence, trier par numéro de page (ascendant)
          return (parseInt(a.pageNumber) || 0) - (parseInt(b.pageNumber) || 0);
        });
      
      console.log(`Recherche vectorielle: ${sortedResults.length} résultats`);
      updateFilteredPassages(sortedResults, null, true);
      return sortedResults;
      
    } catch (error) {
      console.error("Erreur lors de la recherche:", error);
      // En cas d'erreur, revenir à une recherche simple
      const simpleResults = passages.filter(passage => 
        passage.content.toLowerCase().includes(query.toLowerCase())
      );
      updateFilteredPassages(simpleResults, null, true);
      return simpleResults;
    } finally {
      setIsSearching(false);
    }
  }, [passages, documentId, userId, competenceId, makeAuthenticatedRequest, BackEndUrl, updateFilteredPassages]);
  
  // Fonction pour injecter les styles CSS pour les surlignages
  const injectHighlightStyles = useCallback(() => {
    if (!iframeRef || !iframeRef.current) return;
    
    try {
      const iframe = iframeRef.current;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      
      // Vérifier si les styles sont déjà injectés
      if (!iframeDocument.getElementById('pdf-highlight-styles')) {
        const styleEl = iframeDocument.createElement('style');
        styleEl.id = 'pdf-highlight-styles';
        styleEl.textContent = `
          .claude-pdf-highlight {
            position: absolute;
            background-color: rgba(255, 255, 0, 0.3);
            pointer-events: none;
            z-index: 1;
            transition: background-color 0.2s ease-in-out;
          }
          
          @keyframes highlightPulse {
            0% { background-color: rgba(255, 255, 0, 0.3); }
            50% { background-color: rgba(255, 255, 0, 0.5); }
            100% { background-color: rgba(255, 255, 0, 0.3); }
          }
          
          .claude-pdf-highlight.pulse {
            animation: highlightPulse 1.5s ease-in-out 3;
          }
          
          .claude-pdf-highlight-active {
            background-color: rgba(255, 165, 0, 0.4);
            border: 1px solid rgba(255, 165, 0, 0.6);
          }
          
          .claude-pdf-highlight-concept1 {
            background-color: rgba(173, 216, 230, 0.4);
          }
          
          .claude-pdf-highlight-concept2 {
            background-color: rgba(144, 238, 144, 0.4);
          }
          
          .claude-pdf-highlight-concept3 {
            background-color: rgba(255, 182, 193, 0.4);
          }
        `;
        iframeDocument.head.appendChild(styleEl);
        console.log("Styles de surlignage injectés dans l'iframe");
      }
    } catch (error) {
      console.error("Erreur lors de l'injection des styles de surlignage:", error);
    }
  }, [iframeRef]);

  // Fonction pour créer un surlignage sur un élément texte spécifique
  const createHighlightForText = useCallback((textLayer, searchText, isActive = false) => {
    // Obtenir tous les spans de texte dans la couche
    const textSpans = Array.from(textLayer.querySelectorAll('span'));
    
    // Variables pour suivre l'état de la recherche
    let currentTextCollected = '';
    let matchingSpans = [];
    let inMatch = false;
    
    // Parcourir tous les spans pour trouver ceux qui contiennent le texte recherché
    for (let i = 0; i < textSpans.length; i++) {
      const span = textSpans[i];
      const spanText = span.textContent || '';
      
      // Ajouter ce span au texte collecté
      currentTextCollected += spanText + ' ';
      
      // Vérifier si nous avons trouvé le début du texte recherché
      if (!inMatch && currentTextCollected.includes(searchText)) {
        inMatch = true;
      }
      
      // Si nous sommes dans une correspondance, ajouter ce span aux spans correspondants
      if (inMatch) {
        matchingSpans.push(span);
        
        // Vérifier si nous avons atteint la fin du texte recherché
        if (matchingSpans.length >= searchText.length / 4) { // Approximation
          break;
        }
      }
      
      // Si le texte collecté devient trop long sans correspondance, réinitialiser
      if (currentTextCollected.length > searchText.length * 2 && !inMatch) {
        currentTextCollected = currentTextCollected.substring(currentTextCollected.length - searchText.length);
      }
    }
    
    // Créer des surlignages pour tous les spans correspondants
    matchingSpans.forEach(span => {
      const rect = span.getBoundingClientRect();
      const textLayerRect = textLayer.getBoundingClientRect();
      
      // Créer un élément div pour le surlignage
      const highlight = document.createElement('div');
      highlight.className = `claude-pdf-highlight ${isActive ? 'claude-pdf-highlight-active pulse' : ''}`;
      highlight.style.position = 'absolute';
      highlight.style.left = `${span.offsetLeft}px`;
      highlight.style.top = `${span.offsetTop}px`;
      highlight.style.width = `${rect.width}px`;
      highlight.style.height = `${rect.height}px`;
      
      // Ajouter le surlignage à la couche de texte
      textLayer.appendChild(highlight);
    });
    
    return matchingSpans.length > 0;
  }, []);

  // Fonction principale pour mettre en évidence un passage dans le PDF Viewer
  const highlightPassageInViewer = useCallback(async (passage) => {
    if (!passage || !passage.content || !iframeRef || !iframeRef.current) {
      console.log("Impossible de mettre en surbrillance le passage: données manquantes");
      return;
    }

    try {
      // Injecter les styles CSS nécessaires
      injectHighlightStyles();
      
      // Attendre que l'iframe soit complètement chargée
      const iframe = iframeRef.current;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
      
      // S'assurer que le document PDF.js est prêt
      if (!iframeDocument.querySelector("#viewer .textLayer")) {
        console.log("PDF.js viewer n'est pas encore prêt");
        // Tenter à nouveau après un court délai
        setTimeout(() => highlightPassageInViewer(passage), 500);
        return;
      }

      // Nettoyer les surlignages précédents
      const previousHighlights = iframeDocument.querySelectorAll('.claude-pdf-highlight');
      previousHighlights.forEach(el => el.remove());

      // Préparer le texte du passage pour la recherche
      // Nettoyez le texte pour qu'il corresponde mieux au texte dans le PDF
      const searchText = passage.content
        .trim()
        .replace(/\s+/g, ' ')  // Remplacer plusieurs espaces par un seul
        .substring(0, 100);    // Limiter à une longueur raisonnable
      
      if (searchText.length < 10) {
        console.log("Texte de passage trop court pour une mise en surbrillance fiable");
        return;
      }

      // Récupérer toutes les couches de texte sur la page actuelle
      const textLayers = iframeDocument.querySelectorAll(`#viewer .page[data-page-number="${passage.pageNumber}"] .textLayer`);
      
      if (!textLayers || textLayers.length === 0) {
        console.log(`Couche de texte non trouvée pour la page ${passage.pageNumber}`);
        return;
      }

      // Parcourir chaque couche de texte pour trouver le contenu du passage
      let highlightSuccess = false;
      
      textLayers.forEach(textLayer => {
        // Obtenir tout le texte de la couche
        const layerText = textLayer.innerText || '';
        
        // Chercher l'index du passage dans le texte complet
        const startIndex = layerText.indexOf(searchText);
        if (startIndex === -1) {
          // Si exact match ne fonctionne pas, essayer de trouver des phrases clés
          console.log("Texte exact non trouvé, essai avec des segments");
          // Diviser le texte en segments
          const segments = searchText.split(/[.,;:!?] /).filter(s => s.length > 15);
          
          segments.forEach(segment => {
            if (layerText.includes(segment)) {
              console.log("Segment trouvé:", segment);
              // Créer un surlignage pour ce segment
              const success = createHighlightForText(textLayer, segment, true);
              if (success) highlightSuccess = true;
            }
          });
        } else {
          // Créer un surlignage pour le texte complet trouvé
          const success = createHighlightForText(textLayer, searchText, true);
          if (success) highlightSuccess = true;
        }
      });
      
      if (!highlightSuccess) {
        console.log("Aucun texte correspondant trouvé pour le surlignage");
      }
    } catch (error) {
      console.error("Erreur lors de la mise en surbrillance du passage:", error);
    }
  }, [iframeRef, injectHighlightStyles, createHighlightForText]);

  // Fonction pour rendre le contenu d'un passage
  const renderPassageContent = useCallback((passage) => {
    if (!passage) return null;
    
    const isActive = false; // À déterminer selon votre logique d'UI
    
    return (
      <div className={`p-3 mb-2 border rounded-lg ${isActive ? 'bg-blue-50 border-blue-300' : 'border-gray-200'}`}>
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 text-xs rounded-md bg-gray-100 text-gray-700 font-medium">
              Page {passage.pageNumber}
            </span>
            
            {passage.concept && (
              <span className="px-2 py-1 text-xs rounded-md bg-blue-100 text-blue-800">
                {passage.concept}
              </span>
            )}
          </div>
        </div>
        
        <div className="text-sm text-gray-800 mb-2 line-clamp-4">
          {passage.content}
        </div>
      </div>
    );
  }, []);

  // Effets pour initialiser les passages
  useEffect(() => {
    if (initialPassages.length > 0) {
      initializePassages(initialPassages);
    }
  }, [initialPassages, initializePassages]);

  return {
    passages,
    filteredPassages,
    isSearching,
    initializePassages,
    updateFilteredPassages,
    searchInDocument,
    highlightPassageInViewer,
    renderPassageContent
  };
};

export default usePassageManager;